/**
 * Writing a conversation's messages to `conversation_messages`. Composed by the `web-chat` store
 * facade (MAR-948); the streaming composable receives `saveMessage`/`saveMessages`/`updateMessage`
 * from here as its deps.
 *
 * ONE MESSAGE ID, EVERYWHERE: a message's uuid is minted client-side (`generateId`) and is the row's
 * primary key, so client and server never hold two ids for one message (MAR-41). Nothing here
 * returns an id — the caller already has the only one there is.
 */
import type { Ref } from 'vue'
import type { ApiFetch } from '~/composables/useApi'
import type { WebChatMessage } from '~/types/web-chat'
import type { ConversationMessageRecord } from '~~/shared/chat/conversation'
import type { StreamingState } from '~/composables/web-chat/useChatStreaming'

export interface MessagePersistenceDeps {
  conversationId: Ref<string | null>
  messages: Ref<WebChatMessage[]>
  isStreaming: Ref<boolean>
  state: StreamingState
  apiFetch: ApiFetch
  /** Aborts the in-flight turn. */
  cancel: () => void
}

export function useMessagePersistence(deps: MessagePersistenceDeps) {
  const { conversationId, messages, isStreaming, state, apiFetch, cancel } = deps

  /** Resolves to `true` once the rows exist, `null` when there was nothing to write or it failed. */
  async function saveMessages(msgs: WebChatMessage[]): Promise<boolean | null> {
    if (!conversationId.value || msgs.length === 0) return null
    try {
      await apiFetch<ConversationMessageRecord[]>(`/api/conversations/${conversationId.value}/messages`, {
        method: 'POST',
        body: {
          messages: msgs.map(msg => ({ id: msg.id, role: msg.role, content: msg.content, timestamp: msg.timestamp })),
        },
      })
      return true
    } catch (err) {
      console.error('Failed to save messages:', err)
      return null
    }
  }

  /** Persist one message under ITS OWN id; resolves to whether the row exists. */
  async function saveMessage(msg: WebChatMessage): Promise<boolean> {
    return (await saveMessages([msg])) !== null
  }

  async function updateMessage(messageId: string, content: string) {
    if (!conversationId.value) return
    await apiFetch(`/api/conversations/${conversationId.value}/messages`, {
      method: 'PATCH',
      body: { messageId, content },
    }).catch(err => console.error('Failed to update message:', err))
  }

  /**
   * Branch the thread at `messageId` (edit / regenerate), dropping it and everything after.
   *
   * Aborts any in-flight turn FIRST: otherwise the running stream keeps writing to an index that no
   * longer exists ("Cannot read properties of undefined (reading 'content')").
   *
   * The ONLY place the app branches a thread, and this one request is the ONLY thing that deletes
   * from either server-side log: the server drops the message and everything after it from both
   * `conversation_messages` and the ADK session's event log (MAR-41).
   *
   * AWAITED — the caller re-sends immediately, and the resent turn's rows carry a LATER timestamp,
   * so a DELETE still in flight would take them with it. It emptied a conversation on 2026-07-28.
   */
  async function truncateFrom(messageId: string) {
    const idx = messages.value.findIndex(m => m.id === messageId)
    if (idx < 0) return
    if (isStreaming.value) cancel()
    messages.value.splice(idx)
    state.assistantPersisted = false
    if (!conversationId.value) return
    await apiFetch(`/api/conversations/${conversationId.value}/messages?from_message_id=${messageId}`, {
      method: 'DELETE',
    }).catch(err => console.error('Failed to truncate messages:', err))
  }

  return { saveMessage, saveMessages, updateMessage, truncateFrom }
}
